import { CommonModule } from "@angular/common";
import Swal from "sweetalert2";
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";

import {
  CRITERIOS_DAR,
  DarCambio,
  DarCriterio,
  DarInvolucrado,
  ESCALA_DAR,
  SolicitudIADar,
} from "../../shared/model/iso42001/solicitud-ia.model";

/**
 * El DAR en pantalla: FOR-012 Reporte DAR, V 1.2.
 *
 * Es para quien NO sube el Excel. Tiene que quedar exactamente lo mismo
 * que trae el formato, campo por campo: por eso están las tres tablas
 * que se repiten (involucrados, alternativas y criterios) y el historial
 * de cambios de la hoja HCC, y no una versión resumida.
 *
 * EL PUNTAJE Y LOS TOTALES NO SE CAPTURAN NI SE GUARDAN: son peso por
 * calificación. Se calculan aquí a la vista. Guardar un total que se
 * puede calcular es garantizar que algún día no cuadre con sus sumandos.
 */
@Component({
  selector: "app-modal-dar",
  standalone: true,
  templateUrl: "./modal-dar.component.html",
  styleUrls: ["./modal-dar.component.css"],
  imports: [CommonModule, FormsModule, MatTooltipModule],
})
export class ModalDarComponent implements OnInit {
  /** El DAR que ya existía, si lo hay. */
  @Input() dar: SolicitudIADar | null = null;
  /** Sólo lectura cuando la solicitud ya se resolvió. */
  @Input() soloLectura = false;
  /** Quién está llenando el DAR. Va al historial de cambios. */
  @Input() yo = "";

  @Output() guardar = new EventEmitter<SolicitudIADar>();
  @Output() cerrar = new EventEmitter<void>();

  readonly ESCALA = ESCALA_DAR;

  /**
   * Las columnas de alternativas, por INDICE.
   *
   * No se itera sobre `alternativas` directamente: son cadenas, y un
   * *ngFor sin trackBy revienta con "duplicate keys" en cuanto hay dos
   * iguales —dos vacias, sin ir mas lejos—. Y al reventar no falla solo
   * ese bucle: se cae el render del modal entero.
   *
   * Es un CAMPO y no un getter a proposito: un getter que devuelve un
   * arreglo nuevo en cada ciclo de deteccion repinta la matriz sin
   * parar. Se recalcula solo al agregar o quitar una alternativa.
   */
  cols: number[] = [0, 1, 2];

  /**
   * El formato trae tres columnas, pero comparar DOS herramientas es una
   * comparacion legitima y comparar cinco tambien. Se pueden agregar las
   * que hagan falta; por debajo de dos ya no hay nada que comparar.
   */
  readonly MIN_ALTS = 2;

  /**
   * Topes para que el formato no se llene a lo masivo (29/09/2026, Norman:
   * «que tengan máximo también»). Cada casilla ya tenía su largo; ahora las
   * tablas tienen además cuántos renglones. El servidor exige los mismos
   * (SolicitudIABuss.exigeDarCompleto).
   */
  readonly MAX_ALTS = 5;
  readonly MAX_INVOLUCRADOS = 10;
  readonly MAX_CRITERIOS = 12;

  /** El peso de un criterio: entero, del 1 al 5. */
  readonly PESOS = [1, 2, 3, 4, 5];

  /**
   * El ejemplo del tiempo de implementacion.
   *
   * Va aqui y no en el atributo `placeholder` de la plantilla porque
   * tiene saltos de linea: dentro del HTML habria que escribirlo pegado
   * al margen para que no se colaran los espacios de la sangria, y el
   * dia que alguien reacomode la plantilla se rompe sin avisar.
   */
  readonly EJ_TIEMPO =
    "Por ejemplo:\n" +
    "Tres meses\n" +
    "-Plan de implementación\n" +
    "1.Actividad 1\n" +
    "2.Actividad 2";

  /* ---------------- Los campos sueltos ---------------- */
  proyectoArea = "";
  situacionActual = "";
  alternativasTexto = "";
  solucionFinal = "";
  riesgos = "";
  tiempoImplementacion = "";
  costosImplementacion = "";
  recursoHumanoMaterial = "";
  fechaImplementacion = "";
  responsableImplementacion = "";

  /* ---------------- Las tres tablas ---------------- */
  involucrados: DarInvolucrado[] = [];
  /** Tres al abrir, como el formato; se agregan o se quitan. Los vacíos no se guardan. */
  alternativas: string[] = ["", "", ""];
  criterios: DarCriterio[] = [];
  historial: DarCambio[] = [];

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    const d = this.dar;
    if (d) {
      this.proyectoArea = d.proyectoArea || "";
      this.situacionActual = d.situacionActual || "";
      this.alternativasTexto = d.alternativasTexto || "";
      this.solucionFinal = d.solucionFinal || "";
      this.riesgos = d.riesgos || "";
      this.tiempoImplementacion = d.tiempoImplementacion || "";
      this.costosImplementacion = d.costosImplementacion || "";
      this.recursoHumanoMaterial = d.recursoHumanoMaterial || "";
      this.fechaImplementacion = d.fechaImplementacion || "";
      this.responsableImplementacion = d.responsableImplementacion || "";

      this.involucrados = this.lee<DarInvolucrado>(d.involucrados);
      // Todas las que traiga, no solo tres: un DAR guardado con cinco
      // alternativas tiene que volver con las cinco.
      const alts = this.lee<string>(d.alternativas);
      if (alts.length) {
        this.alternativas = alts.map((a) => a || "");
      }
      this.criterios = this.lee<DarCriterio>(d.criterios);
      this.historial = this.lee<DarCambio>(d.historialCambios);
    }

    // Arranques razonables: un DAR en blanco con las ocho filas del
    // formato ya puestas se llena; uno vacío del todo intimida.
    if (!this.involucrados.length) {
      this.involucrados = [{ nombre: "", area: "" }];
    }
    while (this.alternativas.length < this.MIN_ALTS) {
      this.alternativas.push("");
    }
    this.recalculaColumnas();

    if (!this.criterios.length) {
      this.criterios = CRITERIOS_DAR.map((c) => ({
        criterio: c.criterio,
        peso: c.peso,
        calif: this.alternativas.map(() => 0),
      }));
    }
    // Al que venga de la base puede faltarle el arreglo de
    // calificaciones, o traerlo mas corto que las alternativas.
    this.criterios.forEach((c) => {
      if (!c.calif) {
        c.calif = [];
      }
      while (c.calif.length < this.alternativas.length) {
        c.calif.push(0);
      }
    });

    // El control de cambios se escribe solo. Versión, quién lo elaboró y
    // cuándo son datos que el sistema ya conoce: pedírselos a quien
    // llena el formato es invitar a que se equivoque, y en un formato
    // controlado esos tres campos son justo los que tienen que ser
    // fiables. Lo único que se teclea es quién autoriza, porque eso el
    // sistema no lo sabe.
    if (!this.historial.length) {
      this.historial = [
        {
          version: "1.0",
          descripcion: "Emisión inicial del DAR",
          elaboro: this.yo || "",
          autorizo: "",
          fecha: this.hoyISO(),
        },
      ];
    }
  }

  /** La fecha de hoy en aaaa-mm-dd, con el reloj local. */
  private hoyISO(): string {
    const d = new Date();
    const dos = (n: number) => (n < 10 ? "0" + n : "" + n);
    return d.getFullYear() + "-" + dos(d.getMonth() + 1) + "-" + dos(d.getDate());
  }

  /** aaaa-mm-dd -> dd/mm/aaaa, que es como se lee un formato impreso. */
  fechaLegible(iso?: string): string {
    if (!iso) {
      return "";
    }
    const p = iso.substring(0, 10).split("-");
    return p.length === 3 ? p[2] + "/" + p[1] + "/" + p[0] : iso;
  }

  /** Lee una columna JSON sin tumbar la pantalla si viniera mal. */
  private lee<T>(texto?: string): T[] {
    if (!texto) {
      return [];
    }
    try {
      const v = JSON.parse(texto);
      return Array.isArray(v) ? v : [];
    } catch {
      return [];
    }
  }

  /* ---------------- Involucrados ---------------- */

  agregaInvolucrado(): void {
    if (this.involucrados.length >= this.MAX_INVOLUCRADOS) {
      return;
    }
    this.involucrados.push({ nombre: "", area: "" });
    this.cdr.detectChanges();
  }

  quitaInvolucrado(i: number): void {
    this.involucrados.splice(i, 1);
    if (!this.involucrados.length) {
      this.involucrados = [{ nombre: "", area: "" }];
    }
    this.cdr.detectChanges();
  }

  /* ---------------- Criterios ---------------- */

  /**
   * Una alternativa mas: la columna de la matriz nace con todas sus
   * calificaciones en cero, que es lo mismo que "sin calificar".
   */
  agregaAlternativa(): void {
    if (this.alternativas.length >= this.MAX_ALTS) {
      return;
    }
    this.alternativas.push("");
    this.criterios.forEach((c) => {
      if (!c.calif) {
        c.calif = [];
      }
      c.calif.push(0);
    });
    this.recalculaColumnas();
    this.cdr.detectChanges();
  }

  /**
   * Quitar una alternativa se lleva SU columna de la matriz, no la
   * ultima: si no, las calificaciones se recorrerian de herramienta.
   */
  quitaAlternativa(i: number): void {
    if (this.alternativas.length <= this.MIN_ALTS) {
      return;
    }
    this.alternativas.splice(i, 1);
    this.criterios.forEach((c) => {
      if (c.calif) {
        c.calif.splice(i, 1);
      }
    });
    this.recalculaColumnas();
    this.cdr.detectChanges();
  }

  private recalculaColumnas(): void {
    this.cols = this.alternativas.map((_, i) => i);
  }

  agregaCriterio(): void {
    if (this.criterios.length >= this.MAX_CRITERIOS) {
      return;
    }
    this.criterios.push({
      criterio: "",
      peso: 3,
      calif: this.alternativas.map(() => 0),
    });
    this.cdr.detectChanges();
  }

  quitaCriterio(i: number): void {
    this.criterios.splice(i, 1);
    this.cdr.detectChanges();
  }

  /** Peso × calificación. No se guarda: se ve. */
  puntaje(c: DarCriterio, alt: number): number {
    const peso = Number(c.peso) || 0;
    const cal = Number(c.calif && c.calif[alt]) || 0;
    return peso * cal;
  }

  /** El total de una alternativa, para saber cuál gana. */
  total(alt: number): number {
    return this.criterios.reduce((suma, c) => suma + this.puntaje(c, alt), 0);
  }

  /** Cuál va ganando, para resaltarla. Vacío si nadie tiene puntos. */
  get mejor(): number {
    let mejor = -1;
    let max = 0;
    for (let i = 0; i < this.alternativas.length; i++) {
      const t = this.total(i);
      if (t > max) {
        max = t;
        mejor = i;
      }
    }
    return mejor;
  }

  /* ---------------- Historial de cambios ---------------- */

  /**
   * ¿Hay algo escrito? Sirve para no tirar media hora de trabajo con un
   * clic despistado.
   *
   * Se mira lo que TECLEA la gente. El control de cambios no cuenta: la
   * primera fila la rellena solo el componente al abrirse, así que si
   * contara, el formato recién abierto ya parecería tener algo.
   */
  get tieneAlgo(): boolean {
    const texto = [
      this.proyectoArea,
      this.situacionActual,
      this.alternativasTexto,
      this.solucionFinal,
      this.riesgos,
      this.tiempoImplementacion,
      this.costosImplementacion,
      this.responsableImplementacion,
      this.recursoHumanoMaterial,
      this.fechaImplementacion,
    ].some((t) => !!(t || "").trim());
    if (texto) {
      return true;
    }
    if (this.involucrados.some((i) => (i.nombre || "").trim() || (i.area || "").trim())) {
      return true;
    }
    if (this.cols.some((i) => (this.alternativas[i] || "").trim())) {
      return true;
    }
    if (this.historial.some((h) => (h.autorizo || "").trim())) {
      return true;
    }
    return false;
  }

  /**
   * Cerrar. Si hay algo escrito se pregunta, porque este formato son
   * veinte campos, tres involucrados y una matriz de ocho criterios: un
   * clic en la equis para "quitarlo de en medio un momento" se llevaba
   * media hora de captura sin decir ni pío.
   */
  intentaCerrar(): void {
    if (this.soloLectura || !this.tieneAlgo) {
      this.cerrar.emit();
      return;
    }
    Swal.fire({
      title: "¿Cerrar el DAR?",
      text: "Se perderá lo que llevas escrito: el formato no se guarda a medias.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cerrar y perderlo",
      cancelButtonText: "Seguir llenándolo",
    }).then((r) => {
      if (r.isConfirmed) {
        this.cerrar.emit();
      }
    });
  }

  /* ---------------- Lo que falta ----------------

     El DAR pasó a ser obligatorio de cabo a rabo. Antes se podía guardar
     a medias «para irlo completando durante la evaluación», y lo que
     salía de ahí eran formatos con la mitad de las casillas en blanco:
     un DAR incompleto no sirve de evidencia de nada.

     Devuelve la PRIMERA cosa que falta, en el orden en que están en
     pantalla, para que se vaya llenando de arriba abajo. Vacío = listo. */

  get falta(): string {
    if (!this.proyectoArea.trim()) {
      return "Falta el proyecto o área.";
    }
    if (!this.situacionActual.trim()) {
      return "Falta describir la situación actual.";
    }

    // Involucrados: al menos uno, y completo. Media ficha no sirve.
    const conAlgo = this.involucrados.filter(
      (i) => (i.nombre || "").trim() || (i.area || "").trim(),
    );
    if (!conAlgo.length) {
      return "Falta al menos un involucrado.";
    }
    const medio = conAlgo.findIndex(
      (i) => !(i.nombre || "").trim() || !(i.area || "").trim(),
    );
    if (medio >= 0) {
      return `Al involucrado ${medio + 1} le falta el nombre o el área.`;
    }

    if (!this.alternativasTexto.trim()) {
      return "Falta decir qué alternativas se compararon.";
    }

    // Alternativas: TODAS con nombre (29/09/2026, «todos los campos
    // obligatorios»). La que no se vaya a usar se quita con su equis; por
    // debajo de dos ya no hay nada que comparar.
    const sinNombre = this.cols.findIndex((i) => !(this.alternativas[i] || "").trim());
    if (sinNombre >= 0) {
      return `Falta el nombre de la alternativa ${sinNombre + 1} (o quítala).`;
    }
    const cuales = this.cols;

    // Criterios: todos con nombre y con peso del 1 al 5.
    for (let f = 0; f < this.criterios.length; f++) {
      const c = this.criterios[f];
      if (!(c.criterio || "").trim()) {
        return `Al criterio ${f + 1} le falta el nombre.`;
      }
      const peso = Number(c.peso);
      if (!Number.isInteger(peso) || peso < 1 || peso > 5) {
        return `El peso del criterio ${f + 1} va del 1 al 5.`;
      }
    }
    if (!this.criterios.length) {
      return "Falta al menos un criterio para comparar.";
    }

    // Y cada alternativa que SE NOMBRÓ tiene que estar calificada en
    // todos los criterios: una columna a medias no se puede sumar.
    for (const i of cuales) {
      const sinCalif = this.criterios.findIndex(
        (c) => !Number(c.calif && c.calif[i]),
      );
      if (sinCalif >= 0) {
        return `Falta calificar "${this.alternativas[i].trim()}" en el criterio ${sinCalif + 1}.`;
      }
    }

    if (!this.solucionFinal.trim()) {
      return "Falta la solución final.";
    }
    if (!this.riesgos.trim()) {
      return "Faltan los riesgos.";
    }
    if (!this.tiempoImplementacion.trim()) {
      return "Falta el tiempo de implementación.";
    }
    if (!this.costosImplementacion.trim()) {
      return "Faltan los costos de implementación.";
    }
    if (!this.fechaImplementacion) {
      return "Falta la fecha de implementación.";
    }
    if (!this.responsableImplementacion.trim()) {
      return "Falta el responsable de la implementación.";
    }
    if (!this.recursoHumanoMaterial.trim()) {
      return "Falta el recurso humano y material.";
    }

    // Lo único que se teclea del control de cambios.
    const sinAutorizo = this.historial.findIndex((h) => !(h.autorizo || "").trim());
    if (sinAutorizo >= 0) {
      return "Falta quién autoriza, en el control de cambios.";
    }

    return "";
  }

  /* ---------------- Guardar ---------------- */

  /**
   * Guardar. Todo es obligatorio, así que aquí ya no queda nada vacío
   * que filtrar: sólo se recortan los espacios y se quitan las
   * alternativas que no se nombraron, que son las columnas de la matriz
   * que el usuario decidió no usar.
   */
  aceptar(): void {
    // El boton ya esta apagado, pero el guardado no se fia de la vista.
    if (this.falta) {
      return;
    }
    const involucrados = this.involucrados.filter(
      (i) => (i.nombre || "").trim() || (i.area || "").trim(),
    );
    const alternativas = this.alternativas
      .map((a) => (a || "").trim())
      .filter(Boolean);
    const criterios = this.criterios.filter((c) => (c.criterio || "").trim());
    // El historial se escribe solo, asi que va tal cual: filtrar por
    // "tiene algo escrito" tenia sentido cuando lo tecleaban a mano.
    const historial = this.historial;

    const dar: SolicitudIADar = {
      idSolicitudIa: this.dar ? this.dar.idSolicitudIa : undefined,
      proyectoArea: this.proyectoArea.trim() || undefined,
      situacionActual: this.situacionActual.trim() || undefined,
      alternativasTexto: this.alternativasTexto.trim() || undefined,
      solucionFinal: this.solucionFinal.trim() || undefined,
      riesgos: this.riesgos.trim() || undefined,
      tiempoImplementacion: this.tiempoImplementacion.trim() || undefined,
      costosImplementacion: this.costosImplementacion.trim() || undefined,
      recursoHumanoMaterial: this.recursoHumanoMaterial.trim() || undefined,
      fechaImplementacion: this.fechaImplementacion || undefined,
      responsableImplementacion: this.responsableImplementacion.trim() || undefined,
      // Las cuatro viajan como texto JSON, que es como viven en la base.
      involucrados: involucrados.length ? JSON.stringify(involucrados) : undefined,
      alternativas: alternativas.length ? JSON.stringify(alternativas) : undefined,
      criterios: criterios.length ? JSON.stringify(criterios) : undefined,
      historialCambios: historial.length ? JSON.stringify(historial) : undefined,
    };
    this.guardar.emit(dar);
  }


  trackIndice = (i: number) => i;
}
